import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { tasksApi, mattersApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { CheckSquare, Plus, TrashSimple, WarningOctagon, Briefcase, Calendar } from "@phosphor-icons/react";
import { toast } from "sonner";
import HelpButton from "@/components/HelpButton";

const PRIORITY_RANK = { urgent: 0, high: 1, medium: 2, low: 3 };

function bucketize(tasks) {
    const today = new Date().toISOString().slice(0, 10);
    const in7 = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
    const overdue = [];
    const dueSoon = [];
    const later = [];
    const noDate = [];
    const done = [];
    for (const t of tasks) {
        if (t.status === "done") { done.push(t); continue; }
        if (!t.due_date) { noDate.push(t); continue; }
        if (t.due_date < today) overdue.push(t);
        else if (t.due_date <= in7) dueSoon.push(t);
        else later.push(t);
    }
    const sort = (arr) => arr.sort((a, b) => {
        if (a.due_date !== b.due_date) return (a.due_date || "9999") < (b.due_date || "9999") ? -1 : 1;
        return (PRIORITY_RANK[a.priority] ?? 9) - (PRIORITY_RANK[b.priority] ?? 9);
    });
    [overdue, dueSoon, later, noDate, done].forEach(sort);
    return { overdue, dueSoon, later, noDate, done };
}

export default function Tasks() {
    const [tasks, setTasks] = useState([]);
    const [matters, setMatters] = useState([]);
    const [open, setOpen] = useState(false);
    const [filterMatter, setFilterMatter] = useState("all");
    const [showDone, setShowDone] = useState(false);

    const load = async () => {
        try {
            const params = {};
            if (filterMatter !== "all") params.matter_id = filterMatter;
            const arr = await tasksApi.list(params);
            setTasks(arr);
        } catch {
            toast.error("Failed to load tasks");
        }
    };
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterMatter]);

    useEffect(() => {
        mattersApi.list().then(setMatters).catch(() => {});
    }, []);

    const toggleDone = async (t) => {
        try {
            const next = t.status === "done" ? "open" : "done";
            const updated = await tasksApi.update(t.id, { status: next });
            setTasks((arr) => arr.map((x) => (x.id === t.id ? updated : x)));
        } catch {
            toast.error("Update failed");
        }
    };
    const remove = async (t) => {
        try {
            await tasksApi.remove(t.id);
            setTasks((arr) => arr.filter((x) => x.id !== t.id));
        } catch {
            toast.error("Delete failed");
        }
    };
    const buckets = bucketize(tasks);

    return (
        <div className="p-4 md:p-8 max-w-[1400px] mx-auto" data-testid="tasks-page">
            <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
                <div className="rule-line pt-3">
                    <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-1">Practice</div>
                <div className="flex items-center gap-2">
                    <h1 className="font-serif text-4xl tracking-tight">Tasks & deadlines</h1>
                    <HelpButton topic="tasks" testid="help-tasks" />
                </div>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid="new-task-btn"><Plus size={14} /> New task</Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>New task</DialogTitle>
                            <DialogDescription>Link to a matter so it appears on the matter timeline.</DialogDescription>
                        </DialogHeader>
                        <NewTaskForm matters={matters} onDone={(t) => { setOpen(false); setTasks((arr) => [t, ...arr]); }} />
                    </DialogContent>
                </Dialog>
            </div>

            <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Select value={filterMatter} onValueChange={setFilterMatter}>
                    <SelectTrigger className="w-[260px]" data-testid="task-matter-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All matters</SelectItem>
                        {matters.map((m) => <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>)}
                    </SelectContent>
                </Select>
                <label className="text-sm inline-flex items-center gap-2">
                    <input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} className="accent-amber-600" /> Show completed
                </label>
            </div>

            <Bucket title="Overdue" tone="destructive" tasks={buckets.overdue} matters={matters} onToggle={toggleDone} onRemove={remove} icon={WarningOctagon} />
            <Bucket title="Due this week" tone="amber" tasks={buckets.dueSoon} matters={matters} onToggle={toggleDone} onRemove={remove} icon={Calendar} />
            <Bucket title="Later" tone="default" tasks={buckets.later} matters={matters} onToggle={toggleDone} onRemove={remove} icon={Calendar} />
            <Bucket title="No due date" tone="default" tasks={buckets.noDate} matters={matters} onToggle={toggleDone} onRemove={remove} icon={CheckSquare} />
            {showDone && <Bucket title="Completed" tone="muted" tasks={buckets.done} matters={matters} onToggle={toggleDone} onRemove={remove} icon={CheckSquare} />}
        </div>
    );
}

function Bucket({ title, tone, tasks, matters, onToggle, onRemove, icon: Icon }) {
    if (!tasks.length) return null;
    const toneClasses = {
        destructive: "border-destructive/40 bg-destructive/5 text-destructive",
        amber: "border-amber-300 bg-amber-50/40 text-amber-900",
        muted: "border-border bg-muted/40 text-muted-foreground",
        default: "border-border bg-white text-foreground",
    }[tone];
    return (
        <section className="mb-6" data-testid={`bucket-${title.toLowerCase().replace(/\s/g, "-")}`}>
            <div className={`inline-flex items-center gap-2 px-3 py-1 border rounded-sm text-xs font-medium uppercase tracking-[0.2em] mb-2 ${toneClasses}`}>
                <Icon size={13} weight="fill" /> {title} <span className="opacity-70">({tasks.length})</span>
            </div>
            <ul className="border border-border bg-white rounded-md divide-y divide-border">
                {tasks.map((t) => {
                    const matter = matters.find((m) => m.id === t.matter_id);
                    return (
                        <li key={t.id} className="p-3 flex items-center gap-3" data-testid={`task-row-${t.id}`}>
                            <input
                                type="checkbox"
                                checked={t.status === "done"}
                                onChange={() => onToggle(t)}
                                className="accent-emerald-600 mt-0.5 h-4 w-4"
                                data-testid={`task-toggle-${t.id}`}
                            />
                            <div className="flex-1 min-w-0">
                                <div className={`text-sm font-medium ${t.status === "done" ? "line-through text-muted-foreground" : ""}`}>{t.title}</div>
                                {t.description && <div className="text-xs text-muted-foreground line-clamp-1">{t.description}</div>}
                                <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground flex-wrap">
                                    <span className={`chip ${t.priority === "urgent" || t.priority === "high" ? "chip-doc text-destructive" : "chip-ai"}`}>{t.priority}</span>
                                    {t.due_date && <span>due {t.due_date}</span>}
                                    {matter && (
                                        <Link to={`/matters/${matter.id}`} className="inline-flex items-center gap-1 hover:underline">
                                            <Briefcase size={11} /> {matter.title}
                                        </Link>
                                    )}
                                </div>
                            </div>
                            <button className="text-muted-foreground hover:text-destructive" onClick={() => onRemove(t)} aria-label="Delete" data-testid={`task-delete-${t.id}`}>
                                <TrashSimple size={14} />
                            </button>
                        </li>
                    );
                })}
            </ul>
        </section>
    );
}

function NewTaskForm({ matters, onDone }) {
    const [v, setV] = useState({ title: "", description: "", due_date: "", priority: "medium", matter_id: "none" });
    const submit = async () => {
        if (!v.title.trim()) { toast.error("Title required"); return; }
        try {
            const t = await tasksApi.create({
                title: v.title, description: v.description, due_date: v.due_date || null,
                priority: v.priority, matter_id: v.matter_id === "none" ? null : v.matter_id,
            });
            toast.success("Task created");
            onDone?.(t);
        } catch {
            toast.error("Failed to create");
        }
    };
    return (
        <div className="space-y-3" data-testid="new-task-form">
            <Input placeholder="Task title" value={v.title} onChange={(e) => setV({ ...v, title: e.target.value })} data-testid="task-title" />
            <Textarea rows={2} placeholder="Optional description / context" value={v.description} onChange={(e) => setV({ ...v, description: e.target.value })} data-testid="task-desc" />
            <div className="grid grid-cols-2 gap-2">
                <Input type="date" value={v.due_date} onChange={(e) => setV({ ...v, due_date: e.target.value })} data-testid="task-due" />
                <Select value={v.priority} onValueChange={(p) => setV({ ...v, priority: p })}>
                    <SelectTrigger data-testid="task-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="urgent">Urgent</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <Select value={v.matter_id} onValueChange={(m) => setV({ ...v, matter_id: m })}>
                <SelectTrigger data-testid="task-matter"><SelectValue placeholder="Link to a matter (optional)" /></SelectTrigger>
                <SelectContent>
                    <SelectItem value="none">No matter</SelectItem>
                    {matters.map((m) => <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>)}
                </SelectContent>
            </Select>
            <div className="flex justify-end">
                <Button onClick={submit} data-testid="task-submit">Create task</Button>
            </div>
        </div>
    );
}
